import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { SnackbarContext } from '../contexts';

import {
  getSelectorValue,
  clientContextWarn,
  hasGraphQLError,
  isUnauthorizedNetworkError,
  redirectLogin,
} from '../utils';
import config from '../config';

const useEnhancedQuery = (query, props = {
  options: {},
  defaultData: {},
  selector: () => {},
  disableDefaultError: false,
}) => {
  const snackbarContext = useContext(SnackbarContext);
  const { setOpenSnackbar } = snackbarContext.actions;
  const history = useHistory();

  const handleOnError = (err) => {
    // UnauthorizedException
    if (isUnauthorizedNetworkError(err)) {
      window.location.href = `${config.app.secureUrl}${redirectLogin()}`;
      return;
    }

    if (!props.disableDefaultError) {
      if (hasGraphQLError(err, 'forbidden')) {
        history.push('/forbidden');
        return;
      }
      if (hasGraphQLError(err, 'item not found')) {
        history.push('/not_found');
        return;
      }
      if (hasGraphQLError(err, 'item has been deleted')) {
        history.push('/item_deleted');
        return;
      }
      setOpenSnackbar({
        variant: 'error',
        message: 'An error has occured. Please try again.',
      });
      clientContextWarn(err);
    }
    if (props.options && props.options.onError) {
      props.options.onError(err);
    }
  };

  const result = useQuery(query, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    ...props.options,
    // Must come after the spread: a caller-supplied options.onError is invoked from inside
    // handleOnError (above), so letting it replace the handler here would skip the 401 redirect
    // and the forbidden / not-found routing for that caller.
    onError: handleOnError,
  });

  const newData = getSelectorValue({ ...props.defaultData, ...result.data }, props.selector);
  return {
    ...result,
    data: newData,
  };
};

export default useEnhancedQuery;
